import { Observable } from 'rxjs';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import * as HrModels from '../hr.models';
import { Constructor, HrApiClient } from '../shared/hr-api-client';

export function withHrLifecycleApi<TBase extends Constructor<HrApiClient>>(Base: TBase) {
  return class extends Base {
    lifecycleDashboard(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrLifecycleDashboard>> {
        return this.http.get<APIResponseWrapper<HrModels.HrLifecycleDashboard>>(`${this.baseUrl}/lifecycle/dashboard`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    onboardingTemplates(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrOnboardingTemplate[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrOnboardingTemplate[]>>(`${this.baseUrl}/onboarding/templates`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    saveOnboardingTemplate(request: HrModels.HrOnboardingTemplate): Observable<APIResponseWrapper<HrModels.HrOnboardingTemplate>> {
        return request.templateId
          ? this.http.put<APIResponseWrapper<HrModels.HrOnboardingTemplate>>(`${this.baseUrl}/onboarding/templates/${request.templateId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrOnboardingTemplate>>(`${this.baseUrl}/onboarding/templates`, request);
      }
    
    startOnboarding(employeeId: number, request: HrModels.HrStartOnboardingRequest = {}): Observable<APIResponseWrapper<HrModels.HrOnboardingPlan>> {
        return this.http.post<APIResponseWrapper<HrModels.HrOnboardingPlan>>(`${this.baseUrl}/employees/${employeeId}/onboarding/start`, request);
      }
    
    employeeOnboarding(employeeId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrOnboardingPlan[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrOnboardingPlan[]>>(`${this.baseUrl}/employees/${employeeId}/onboarding`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    completeOnboardingTask(taskId: number, request: HrModels.HrCompleteTaskRequest = { taskStatus: 'COMPLETED' }): Observable<APIResponseWrapper<HrModels.HrOnboardingTask>> {
        return this.http.post<APIResponseWrapper<HrModels.HrOnboardingTask>>(`${this.baseUrl}/onboarding/tasks/${taskId}/complete`, request);
      }
    
    activationReadiness(employeeId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrActivationReadiness>> {
        return this.http.get<APIResponseWrapper<HrModels.HrActivationReadiness>>(`${this.baseUrl}/employees/${employeeId}/activation-readiness`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    activateEmployee(employeeId: number, request: HrModels.HrEmployeeActionInput = {}): Observable<APIResponseWrapper<HrModels.HrEmployee>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployee>>(`${this.baseUrl}/employees/${employeeId}/activate`, request);
      }
    
    inviteEmployee(employeeId: number, request: HrModels.HrInviteEmployeeRequest = {}): Observable<APIResponseWrapper<HrModels.HrEmployee>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployee>>(`${this.baseUrl}/employees/${employeeId}/invite`, request);
      }
    
    actionRequests(owningCompanyId?: number, status?: string, actionType?: string): Observable<APIResponseWrapper<HrModels.HrEmployeeActionRequest[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrEmployeeActionRequest[]>>(`${this.baseUrl}/actions`, {
          params: this.params({ owningCompanyId, status, actionType })
        });
      }
    
    requestTransfer(employeeId: number, request: HrModels.HrEmployeeActionInput): Observable<APIResponseWrapper<HrModels.HrEmployeeActionRequest>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployeeActionRequest>>(`${this.baseUrl}/employees/${employeeId}/actions/transfer/request`, request);
      }
    
    requestCompensationChange(employeeId: number, request: HrModels.HrEmployeeActionInput): Observable<APIResponseWrapper<HrModels.HrEmployeeActionRequest>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployeeActionRequest>>(`${this.baseUrl}/employees/${employeeId}/actions/compensation/request`, request);
      }
    
    approveAction(actionRequestId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrEmployeeActionRequest>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployeeActionRequest>>(`${this.baseUrl}/actions/${actionRequestId}/approve`, null, {
          params: this.params({ owningCompanyId })
        });
      }
    
    suspendEmployee(employeeId: number, request: HrModels.HrEmployeeActionInput = {}): Observable<APIResponseWrapper<HrModels.HrEmployee>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployee>>(`${this.baseUrl}/employees/${employeeId}/suspend`, request);
      }
    
    reinstateEmployee(employeeId: number, request: HrModels.HrEmployeeActionInput = {}): Observable<APIResponseWrapper<HrModels.HrEmployee>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployee>>(`${this.baseUrl}/employees/${employeeId}/reinstate`, request);
      }
    
    requestTermination(employeeId: number, request: HrModels.HrEmployeeActionInput): Observable<APIResponseWrapper<HrModels.HrEmployeeActionRequest>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployeeActionRequest>>(`${this.baseUrl}/employees/${employeeId}/termination/request`, request);
      }
    
    approveTermination(actionRequestId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrEmployeeActionRequest>> {
        return this.http.post<APIResponseWrapper<HrModels.HrEmployeeActionRequest>>(`${this.baseUrl}/actions/${actionRequestId}/termination/approve`, null, {
          params: this.params({ owningCompanyId })
        });
      }
    
    employeeOffboarding(employeeId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrOffboardingPlan[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrOffboardingPlan[]>>(`${this.baseUrl}/employees/${employeeId}/offboarding`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    completeOffboardingTask(taskId: number, request: HrModels.HrCompleteTaskRequest = { taskStatus: 'COMPLETED' }): Observable<APIResponseWrapper<HrModels.HrOffboardingTask>> {
        return this.http.post<APIResponseWrapper<HrModels.HrOffboardingTask>>(`${this.baseUrl}/offboarding/tasks/${taskId}/complete`, request);
      }
    
    completeOffboarding(employeeId: number, request: HrModels.HrEmployeeActionInput = {}): Observable<APIResponseWrapper<HrModels.HrOffboardingPlan>> {
        return this.http.post<APIResponseWrapper<HrModels.HrOffboardingPlan>>(`${this.baseUrl}/employees/${employeeId}/offboarding/complete`, request);
      }
  };
}

